import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Alert, AlertDescription } from '../ui/Alert';
import { displayError } from '../../lib/error-handler';
import type { Prompt } from '../../types/api';

interface PromptEditorProps {
  prompt: Prompt;
  onSave: (promptText: string, name?: string) => Promise<void>;
  disabled?: boolean;
}

export function PromptEditor({ prompt, onSave, disabled }: PromptEditorProps) {
  const [promptText, setPromptText] = useState(prompt.prompt_text);
  const [name, setName] = useState(prompt.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setPromptText(prompt.prompt_text);
    setName(prompt.name);
  }, [prompt]);

  const hasChanges = promptText !== prompt.prompt_text || name !== prompt.name;

  const handleSave = async () => {
    if (!promptText.trim()) {
      setError('Prompt text cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await onSave(promptText, name);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(displayError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPromptText(prompt.prompt_text);
    setName(prompt.name);
    setError(null);
    setSuccess(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analysis Criteria</CardTitle>
        <CardDescription>
          Define the criteria for analyzing RSS articles. This prompt will be used by AI to score articles.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>Prompt saved successfully!</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="prompt-name">Prompt Name</Label>
          <Input
            id="prompt-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled || saving}
            placeholder="My Analysis Criteria"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="prompt-text">Prompt Text</Label>
          <Textarea
            id="prompt-text"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            disabled={disabled || saving}
            rows={15}
            className="font-mono text-sm"
            placeholder="Enter your analysis criteria..."
          />
          <p className="text-xs text-muted-foreground">
            {promptText.length} characters • Last updated: {new Date(prompt.updated_at).toLocaleDateString()}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || disabled || saving}
          >
            {saving ? 'Saving...' : 'Save Prompt'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || disabled || saving}
          >
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
